import { useCallback } from 'react';
import { useToggle } from '@direct-frontend/stdlib';
import { type OpenApiSchemaImportTypeSettings } from '#shared/api/schema';
// eslint-disable-next-line @direct-frontend/modules/no-restricted-entity-imports -- Ругается на "Modal"
import { useInventoryCompareProductsModalContext } from '#shared/components/InventoryCompareProductsModalContext';
import {
    checkChosenOzonLogisticFbsIntegrationSubType,
    INTEGRATION_TYPE_1C,
    INTEGRATION_TYPE_MOYSKLAD,
    isIntegrationSettingsForStocks,
    type SupportedIntegrationSubTypes,
    type SupportedIntegrationTypes,
    useSourceBlockContext,
} from '#shared/lib/integrations';
import { showError } from '#shared/lib/toast';
import { getIntegrationSettingsProducts } from '../../../api';
import { useGetIntegrationSettings } from '../../../hooks/useGetIntegrationSettings';
import { deleteIntegrationSettingsWithRefetch } from '../../../lib/deleteIntegrationSettingsWithRefetch';
import { ENDPOINT_UPDATE_INTEGRATION_SETTINGS } from '../../../lib/integrationSettingsUpdateEndpoints';
import { useOzonFbsChangeConfirm } from './useOzonFbsChangeConfirm';

type UseInventoryChangeActionsParams = {
    additionalIntegrationType?: SupportedIntegrationSubTypes;
    onAfterDelete?: VoidFunction;
    onOpenCompareWarehousesModal?: VoidFunction;
    onAfterChangeSource?: (type: SupportedIntegrationTypes) => void;
};

export const useInventoryChangeActions = ({
    additionalIntegrationType,
    onAfterDelete,
    onOpenCompareWarehousesModal,
    onAfterChangeSource,
}: UseInventoryChangeActionsParams) => {
    const { value: isSaving, toggleOn: startSaving, toggleOff: stopSaving } = useToggle(false);
    const { data, refetch } = useGetIntegrationSettings();
    const { syncType, modalSettingsCompareByOn, modalSettingsCompareExtendedByOn } = useSourceBlockContext();
    const { open: openCompareModal } = useInventoryCompareProductsModalContext();
    const { handleConfirmedChange, renderConfirmModal } = useOzonFbsChangeConfirm(data);
    const { system_type: systemType } = data || {};

    const isOzonFbsConnectedInStocks =
        isIntegrationSettingsForStocks('stocks', data) && checkChosenOzonLogisticFbsIntegrationSubType(data?.sub_types);

    const afterUpdateActions = useCallback(
        async (systemTypeToUpdate: SupportedIntegrationTypes) => {
            onAfterChangeSource?.(systemTypeToUpdate);

            if (syncType === 'products') {
                modalSettingsCompareExtendedByOn.open(systemTypeToUpdate);

                return;
            }

            try {
                const { system_type: productsSystemType } = await getIntegrationSettingsProducts();

                if (productsSystemType !== systemTypeToUpdate) {
                    modalSettingsCompareByOn.open(systemTypeToUpdate);
                }

                /**
                 * Если гранула остатков и подключен Ozon FBS,
                 * также происходит смена источника на МойСклад или 1С,
                 * то открывается модальное окно для сопоставления складов
                 * setTimeout нужен, чтобы две модалки не открывались в одном рендере, а последовательно одна за другой
                 */
                if (
                    syncType === 'stocks' &&
                    isOzonFbsConnectedInStocks &&
                    (systemTypeToUpdate === INTEGRATION_TYPE_1C || systemTypeToUpdate === INTEGRATION_TYPE_MOYSKLAD)
                ) {
                    setTimeout(() => onOpenCompareWarehousesModal?.(), 0);
                }
            } catch (_) {
                // ignore
            }
        },
        [
            isOzonFbsConnectedInStocks,
            onOpenCompareWarehousesModal,
            modalSettingsCompareByOn,
            modalSettingsCompareExtendedByOn,
            onAfterChangeSource,
            syncType,
        ]
    );

    const handleDelete = useCallback(async () => {
        handleConfirmedChange(async () => {
            startSaving();

            try {
                if (additionalIntegrationType && isIntegrationSettingsForStocks('stocks', data)) {
                    await ENDPOINT_UPDATE_INTEGRATION_SETTINGS[syncType]({
                        sub_types: (data?.sub_types ?? []).filter(
                            ({ sub_type }) => sub_type !== additionalIntegrationType
                        ),
                    });

                    await refetch?.();
                } else {
                    await deleteIntegrationSettingsWithRefetch({ syncType, refetch, onAfterDelete });
                }
            } catch (error) {
                showError({ label: 'Произошла ошибка при отключении источника' }, error);
            } finally {
                stopSaving();
            }
        }, additionalIntegrationType ?? systemType);
    }, [
        handleConfirmedChange,
        startSaving,
        additionalIntegrationType,
        systemType,
        syncType,
        refetch,
        onAfterDelete,
        stopSaving,
        data,
    ]);

    const handleChangeSource = useCallback(
        async (systemTypeToUpdate: SupportedIntegrationTypes) => {
            handleConfirmedChange(async () => {
                if (!syncType) {
                    return;
                }

                openCompareModal(systemTypeToUpdate, async ({ mapping, createStrategy }) => {
                    const payload: OpenApiSchemaImportTypeSettings = {
                        system_type: systemTypeToUpdate,
                        mapping,
                        create_strategy: createStrategy,
                    };

                    startSaving();

                    try {
                        await ENDPOINT_UPDATE_INTEGRATION_SETTINGS[syncType](payload);

                        await refetch?.();

                        await afterUpdateActions(systemTypeToUpdate);
                    } catch (error) {
                        showError({ label: 'Произошла ошибка при смене источника' }, error);
                    } finally {
                        stopSaving();
                    }
                });
            }, systemType);
        },
        [
            handleConfirmedChange,
            systemType,
            syncType,
            openCompareModal,
            startSaving,
            refetch,
            afterUpdateActions,
            stopSaving,
        ]
    );

    return { isSaving, handleDelete, handleChangeSource, renderConfirmModal };
};
